// Grid / Table view selector for the property list page.
//
// Two visual variants matching the design comps:
//
//  - Desktop: `h-[34px]`, `bg-background` track, `rounded-[10px]`,
//    `p-[3px]`, button padding `5px 10px`, `rounded-[6px]`, text 11.5px.
//    Sits inline in the header next to the search pill.
//
//  - Mobile: `bg-card` track (lives over the page bg), `rounded-[10px]`,
//    `p-[3px]`, button padding `6px 8px`, `rounded-[7px]`, text 11px.
//    Sits to the right of the title in the top row.
//
// State persists in `?view=grid|table` so the choice survives refresh
// and is shareable via URL.

import { useSearchParams } from "react-router";

import { cn } from "@/lib/utils";
import {
  readView,
  type PropertyView,
} from "@/features/property/utils/property-filter-params";

const VIEWS: ReadonlyArray<{ value: PropertyView; label: string }> = [
  { value: "grid", label: "Lưới" },
  { value: "table", label: "Bảng" },
];

interface PropertyViewToggleProps {
  className?: string;
  variant?: "desktop" | "mobile";
}

export function PropertyViewToggle({
  className,
  variant = "desktop",
}: PropertyViewToggleProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = readView(searchParams);

  const setValue = (next: PropertyView) => {
    if (next === value) return;
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === "grid") {
          out.delete("view");
        } else {
          out.set("view", next);
        }
        return out;
      },
      { replace: true },
    );
  };

  return (
    <div
      role="tablist"
      aria-label="Kiểu hiển thị"
      className={cn(
        "inline-flex items-center gap-1 rounded-[10px] border border-border p-[3px]",
        variant === "mobile"
          ? "bg-card"
          : "h-[34px] bg-background",
        className,
      )}
    >
      {VIEWS.map((v) => {
        const isActive = v.value === value;
        return (
          <button
            key={v.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setValue(v.value)}
            className={cn(
              "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              variant === "mobile"
                ? "rounded-[7px] px-2 py-1.5 text-[11px]"
                : "h-full rounded-[6px] px-2.5 text-[12px]",
              isActive
                ? "bg-foreground font-semibold text-background"
                : "font-medium text-muted-foreground hover:text-foreground",
            )}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}
